/**
 * BoardPack CFO — page de génération/téléchargement/envoi.
 * PDF mensuel aggrégeant Conformité RH + L121-6 + Risques Parc Auto + Indexation.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  BarChart3, FileDown, Mail, Settings2, Send, Loader2, ShieldAlert,
  CheckCircle2, TrendingUp, Info,
} from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function BoardPack() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [settings, setSettings] = useState({ recipient_email: "", cc_emails: [], enabled: true });
  const [recipient, setRecipient] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { (async () => {
    try {
      const r = await api.get("/reports/board-pack/settings");
      setSettings(r.data);
      setRecipient(r.data.recipient_email || "");
      setCcInput((r.data.cc_emails || []).join(", "));
      setEnabled(r.data.enabled !== false);
    } catch (e) { /* silent */ }
  })(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/reports/board-pack/settings", {
        recipient_email: recipient || null,
        cc_emails: ccInput.split(",").map(s => s.trim()).filter(Boolean),
        enabled,
      });
      toast.success("Réglages enregistrés");
      const r = await api.get("/reports/board-pack/settings");
      setSettings(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!recipient) { toast.error("Renseigne un email CFO"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/reports/board-pack/send-now");
      if (data.sent) toast.success(`Dossier CA envoyé à ${data.to}`);
      else toast.info(`Non envoyé : ${data.reason || data.error}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const downloadHref = `/api/reports/board-pack?year=${year}&month=${month}`;

  return (
    <div className="space-y-6" data-testid="board-pack-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          <BarChart3 className="w-6 h-6 text-slate-700" />
          Dossier CA (DAF)
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          PDF mensuel automatique — Conformité RH, L121-6, risques Parc Auto, indexation contractuelle
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileDown className="w-4 h-4" /> Générer le Dossier CA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start md:items-end gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Mois</Label>
              <select id="board-pack-month" name="board-pack-month"
                value={month} onChange={(e) => setMonth(Number(e.target.value))}
                className="h-9 border rounded-md px-2 text-sm bg-white w-40"
                data-testid="board-pack-month">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Année</Label>
              <Input type="number" value={year} min={2020} max={2030}
                onChange={(e) => setYear(Number(e.target.value))}
                className="h-9 w-28" data-testid="board-pack-year" />
            </div>
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="board-pack-download">
              <a href={downloadHref} download>
                <FileDown className="w-4 h-4 mr-1.5" /> Télécharger le PDF
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings2 className="w-4 h-4" /> Envoi automatique mensuel
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Le 1er de chaque mois, Averdy Agency génère et envoie automatiquement le
            Dossier CA au DAF configuré (planification quotidienne intégrée).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs uppercase text-slate-500">Email DAF</Label>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder="daf@monentreprise.fr" type="email"
              data-testid="board-pack-recipient" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">CC (virgules)</Label>
            <Input value={ccInput} onChange={(e) => setCcInput(e.target.value)}
              placeholder="direction@..., president@..."
              data-testid="board-pack-cc" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input id="board-pack-enabled" name="board-pack-enabled"
              type="checkbox" checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="board-pack-enabled" />
            Activer l'envoi automatique du 1er du mois
          </label>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="board-pack-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                       : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
            <Button variant="outline" onClick={sendNow}
              disabled={sending || !recipient}
              data-testid="board-pack-send-now">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <Send className="w-4 h-4 mr-2" />}
              Envoyer maintenant
            </Button>
          </div>
        </CardContent>
      </Card>

      <Alert className="bg-slate-50 border-slate-200">
        <Info className="w-4 h-4 text-slate-600" />
        <AlertDescription className="text-xs text-slate-700">
          <strong>Contenu du Dossier CA</strong> — 4 sections synthétiques :
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            <li>Conformité RH : alertes critiques / avertissement / info, top 8 actions</li>
            <li>L121-6 : amendes non désignées + risque financier gérant</li>
            <li>Parc Auto : véhicules, amendes non payées, permis perdus</li>
            <li>Indexation : contrats à réviser dans les 60 j avec montants</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
